import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useSalon } from "../lib/salon";
import { formatDateTime } from "../lib/time";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, X, Check, ArrowsClockwise } from "@phosphor-icons/react";
import BookingModal from "../components/BookingModal";
import RescheduleModal from "../components/RescheduleModal";
import { toast } from "sonner";
import { useRefresh } from "../context/RefreshContext";

const fmt = formatDateTime;

const STATUS_COLORS = {
  scheduled: "bg-[#D1E0D7] text-[#1A3626]",
  completed: "bg-[#EFEBE3] text-foreground",
  cancelled: "bg-[#EADCD9] text-[#C46B54] line-through",
  no_show: "bg-[#EADCD9] text-[#C46B54]",
};

const FILTERS = [
  { key: "active",    label: "Active",    match: (b) => b.status === "scheduled" },
  { key: "completed", label: "Completed", match: (b) => b.status === "completed" },
  { key: "cancelled", label: "Cancelled", match: (b) => b.status === "cancelled" },
  { key: "all",       label: "All",       match: () => true },
];

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = FILTERS.find((f) => f.key === searchParams.get("filter"))?.key || "active";
  const [statusFilter, setStatusFilter] = useState(initialFilter);
  const [rescheduling, setRescheduling] = useState(null); // BookingDetail or null
  const { bookingsVersion } = useRefresh();
  const salon = useSalon();
  const currency = salon?.currency_symbol || "$";

  const fetchData = async () => {
    const [b, s, sv] = await Promise.all([
      api.get("/bookings").then((r) => r.data),
      api.get("/stylists").then((r) => r.data),
      api.get("/services").then((r) => r.data),
    ]);
    setBookings(b); setStylists(s); setServices(sv);
  };

  useEffect(() => { fetchData(); }, [bookingsVersion]);

  // Reflect URL → state when ?filter changes (e.g. user clicks a Dashboard KPI)
  useEffect(() => {
    const fromUrl = FILTERS.find((f) => f.key === searchParams.get("filter"))?.key;
    if (fromUrl && fromUrl !== statusFilter) setStatusFilter(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reflect state → URL on filter change so the view is shareable / refresh-stable
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (statusFilter && statusFilter !== "active") next.set("filter", statusFilter);
    else next.delete("filter");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const cancel = async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking cancelled");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const complete = async (id) => {
    try {
      await api.post(`/bookings/${id}/complete`);
      toast.success("Marked as completed");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const filterDef = FILTERS.find((f) => f.key === statusFilter) || FILTERS[0];
  const filtered = useMemo(() => {
    return bookings
      .filter((b) => filterDef.match(b))
      .filter((b) => {
        if (!q) return true;
        const hay = `${b.customer?.name} ${b.customer?.phone} ${b.service?.name} ${b.stylist?.name}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      });
  }, [bookings, q, filterDef]);

  const counts = useMemo(() => {
    const c = { active: 0, completed: 0, cancelled: 0, all: bookings.length };
    bookings.forEach((b) => {
      if (b.status === "scheduled") c.active++;
      else if (b.status === "completed") c.completed++;
      else if (b.status === "cancelled") c.cancelled++;
    });
    return c;
  }, [bookings]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline">Bookings</div>
          <h1 className="font-heading text-4xl tracking-tight font-light mt-2">All bookings</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-primary hover:bg-[#2B4F39]" data-testid="bookings-new-btn">
          <Plus size={16} className="mr-1" /> New booking
        </Button>
      </div>

      <div className="surface-card p-4 flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Search customer, service, stylist..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md bg-[#F9F8F6]"
          data-testid="bookings-search"
        />
        <div className="flex gap-1 ml-auto">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`bookings-filter-${f.key}`}
              className={`px-3 py-1.5 text-xs rounded-md border transition-all ${
                statusFilter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border hover:border-primary"
              }`}
            >
              {f.label}
              <span className={`ml-1.5 ${statusFilter === f.key ? "opacity-80" : "text-muted-foreground"}`}>
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="surface-card overflow-hidden">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead className="bg-secondary/40">
            <tr className="text-left">
              <th className="p-3 overline">When</th>
              <th className="p-3 overline">Customer</th>
              <th className="p-3 overline">Service</th>
              <th className="p-3 overline">Stylist</th>
              <th className="p-3 overline">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">No bookings.</td></tr>
            )}
            {filtered.map((b) => (
              <tr key={b.id} className="border-t border-border hover:bg-secondary/30" data-testid={`booking-row-${b.id}`}>
                <td className="p-3 font-mono text-xs">{fmt(b.start_at)}</td>
                <td className="p-3">
                  <div className="font-medium">{b.customer?.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{b.customer?.phone}</div>
                </td>
                <td className="p-3">{b.service?.name}<div className="text-xs text-muted-foreground">{b.duration_minutes} min · {currency}{b.price?.toFixed?.(0)}</div></td>
                <td className="p-3">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: b.stylist?.color }} />
                    {b.stylist?.name}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`inline-block px-2 py-1 rounded-md text-xs ${STATUS_COLORS[b.status] || ""}`}>
                    {b.status}
                  </span>
                </td>
                <td className="p-3 text-right space-x-1 whitespace-nowrap">
                  {b.status === "scheduled" && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRescheduling(b)}
                        data-testid={`booking-reschedule-${b.id}`}
                        className="text-foreground hover:bg-secondary/60"
                      >
                        <ArrowsClockwise size={14} className="mr-1" /> Reschedule
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => complete(b.id)}
                        data-testid={`booking-complete-${b.id}`}
                        className="text-[#1A3626] hover:bg-[#D1E0D7]"
                      >
                        <Check size={14} className="mr-1" /> Complete
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancel(b.id)}
                        data-testid={`booking-cancel-${b.id}`}
                        className="text-destructive hover:text-destructive"
                      >
                        <X size={14} className="mr-1" /> Cancel
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BookingModal
        open={open}
        onOpenChange={setOpen}
        onCreated={fetchData}
        stylists={stylists}
        services={services}
      />
      <RescheduleModal
        open={!!rescheduling}
        onOpenChange={(v) => !v && setRescheduling(null)}
        booking={rescheduling}
        stylists={stylists}
        onRescheduled={fetchData}
      />
    </div>
  );
}
